import { CMSSection } from "@/cms/models";
import {
  getDesktopHeight,
  getMargins,
  getMobileHeight,
  getPaddings,
  getSectionClassName,
} from "@/utils/theme-helper";
import React from "react";
import SectionBackground from "./SectionBackground";
import { responsiveVariables } from "@/utils/visual-style";
import { LookSystemProvider } from "@/components/motion/LookMotion";

const SectionLayout: React.FC<{
  children: React.ReactNode;
  cmsSection: CMSSection<any>;
  /** Its elements reveal one by one on the look system (elementUnit in utils/motion.ts), so its content does not. */
  revealsByElement?: boolean;
  /** Its content is as wide as Afstand og bredde's Indholdsbredde instead of its width class. */
  followsContentWidth?: boolean;
}> = ({
  children,
  cmsSection,
  revealsByElement = false,
  followsContentWidth = false,
}) => {
  const getSizeClassName = () => {
    switch (cmsSection.ui?.width) {
      case "sm":
        return "max-w-screen-sm";
      case "md":
        return "max-w-screen-md";
      case "lg":
        return "max-w-screen-lg";
      case "xl":
        return "max-w-screen-xl";
      case "2xl":
        return "max-w-screen-2xl";
      case "4xl":
        return "max-w-screen-4xl";
      case "full":
        return "w-full";
      default:
        return "max-w-screen-xl";
    }
  };

  const bgImage = cmsSection.specifics?.bgImage ?? cmsSection.ui?.bgImage ?? "";
  const enhanced = !!(
    cmsSection.ui.background ||
    cmsSection.ui.viewport ||
    cmsSection.ui.minHeight
  );
  const sectionClass = getSectionClassName(cmsSection.id);
  const look = cmsSection.lookAttributes;

  return (
    <section
      data-section-type={cmsSection?.type}
      data-section-role={cmsSection.role}
      data-re-bg={look?.["data-re-bg"]}
      data-re-framed={look?.frame ? "" : undefined}
      {...look?.motion}
      className={`flex flex-1 ${sectionClass} ${getMargins(cmsSection.ui.margin)}`}
      style={{
        backgroundImage:
          !cmsSection.ui.background && bgImage ? `url('${bgImage}')` : "none",
        backgroundSize: cmsSection.ui.bgSize ?? "cover",
        backgroundRepeat: "no-repeat",
        backgroundPositionX: cmsSection.ui.backgroundPositionX ?? "center",
        backgroundPositionY: cmsSection.ui.backgroundPositionY ?? "center",
        ...look?.style,
        ...look?.frame,
      }}
    >
      <style>
        {` ${`.${sectionClass}`} {
        --section-text-color: ${cmsSection.specifics?.textColor ?? cmsSection.ui?.textColor ?? cmsSection.specifics?.farvetekst ?? "inherit"};
        --section-bg-color: ${look?.background ?? cmsSection.specifics?.bgColor ?? cmsSection.ui?.bgColor ?? cmsSection.specifics?.farvebaggrund ?? "transparent"};
        --section-desktop-height: ${getDesktopHeight(cmsSection.ui?.height)};
        --section-mobile-height: ${getMobileHeight(cmsSection.ui?.height)};
        }`}
      </style>
      <div
        style={
          enhanced
            ? responsiveVariables(
                "section-min-height",
                cmsSection.ui.minHeight,
                "0px",
              )
            : undefined
        }
        className={`flex flex-1 justify-center ${enhanced ? "re-section" : ""} ${cmsSection.ui.viewport ? "re-section-viewport" : "h-[var(--section-mobile-height)] md:h-[var(--section-desktop-height)]"} bg-[var(--section-bg-color)] w-full text-[var(--section-text-color)] relative`}
      >
        {cmsSection.ui.background && (
          <SectionBackground
            image={bgImage}
            background={cmsSection.ui.background}
          />
        )}
        <div
          className={`${enhanced ? "re-section-content" : ""} ${followsContentWidth ? "" : getSizeClassName()} w-full flex-1 ${getPaddings(cmsSection.ui?.padding)}`}
          style={
            followsContentWidth
              ? { maxWidth: "var(--re-content-width, 1280px)" }
              : undefined
          }
          data-re-reveal={look?.motion && !revealsByElement ? "" : undefined}
        >
          {look?.motion ? (
            <LookSystemProvider motion={look.motion}>
              {children}
            </LookSystemProvider>
          ) : (
            children
          )}
        </div>
      </div>
    </section>
  );
};

export default SectionLayout;
